import React from 'react';

import { PlanLogo } from './PlanLogo';
import { PlanStatus, PlanSummary } from './planTypes';
import { TEXTOS_DE_NAVEGACION, TextosDeNavegacion } from './navTextos';
import { planDisplayName, planSubtitle } from './planDisplayHelpers';
import { SidebarSelector } from './SidebarSelector';

/**
 * Pieza del Sidebar: el desplegable para elegir plan. Recreación ESTÁTICA del
 * que vive en `src/components/Navigation/DesktopSidebarV2.tsx` — **ya
 * mergeado a master** (chirigota Release 9.2.0, verificado 3 sep 2026). Solo existe en escritorio: en
 * móvil (`MobileBottomNavV2.tsx`, real, también mergeado) plan y rol NO son un desplegable duplicado
 * (ver `MobileTopBar.tsx` y `MobileBottomNav.tsx`). Ver Sidebar.tsx para cómo se compone con el resto de
 * piezas (Header/Nav/UserMenu).
 *
 * Lo que se ve lo pone `SidebarSelector` (la pieza común con el selector de empresas de Admin). Aquí
 * solo se traducen los planes y se decide qué dice y qué hace según lo que tiene la persona:
 *
 * - Con VARIOS planes: el desplegable, con «Resumen de planes» (la pantalla de aterrizaje multicuenta)
 *   arriba del todo, después los planes y, al final, el botón secundario «Añadir plan». Si se está en el resumen y no hay
 *   ningún plan elegido, el botón dice «Resumen de planes» en vez de «Seleccionar plan», que solo queda
 *   para cuando de verdad no hay nada.
 * - Con UN SOLO plan no hay resumen al que ir. Si llega `onAddPlan`, hay desplegable: con ese plan,
 *   marcado, y el botón «Añadir plan», que es la forma de pasar a ser multicuenta. Sin él no hay nada
 *   que hacer al abrirlo, así que es el campo del plan, sin desplegable (como la empresa única en Admin).
 *
 * Elegir un plan aquí es 100% cosmético (cambia el check verde), igual que en el código real: «no hay
 * backend que distinga entre planes individuales» (comentario literal de DesktopSidebarV2.tsx). El real
 * solo muestra este selector para cuentas AccountType.BOTH y `plans` ya llega filtrado/ordenado por
 * quien monta el selector.
 */

export interface SidebarPlanSelectorProps {
	plans: PlanSummary[];
	activePlanId?: string;
	onSelectPlan: (plan: PlanSummary) => void;
	isMenuOpen: boolean;
	/** Se está en el resumen de planes (la pantalla de aterrizaje multicuenta). */
	isSummaryActive?: boolean;
	/** Ir al resumen de planes. Sin él, la fila no se enseña. */
	onSelectSummary?: () => void;
	/** Añadir un plan: el botón secundario del final del desplegable. Sin él, no se enseña. */
	onAddPlan?: () => void;
	textos?: Pick<
		TextosDeNavegacion,
		'seleccionarPlan' | 'selectorDePlan' | 'resumenDePlanes' | 'anadirPlan' | 'enProceso'
	>;
}

export const SidebarPlanSelector = ({
	plans,
	activePlanId,
	onSelectPlan,
	isMenuOpen,
	isSummaryActive = false,
	onSelectSummary,
	onAddPlan,
	textos = TEXTOS_DE_NAVEGACION
}: SidebarPlanSelectorProps) => {
	const unico = plans.length === 1;

	return (
		<SidebarSelector
			options={plans.map((plan) => ({
				id: plan.id,
				// Con la empresa en el de empleo: dos planes de empleo se llaman igual.
				label: planDisplayName(plan),
				subtitle: planSubtitle(plan),
				estado: plan.status === PlanStatus.IN_PROGRESS ? textos.enProceso : undefined,
				logo: (className) => <PlanLogo plan={plan} className={className} />
			}))}
			// Con un solo plan no hay elección: es ese.
			activeId={unico ? plans[0].id : activePlanId}
			onSelect={(id) => {
				const plan = plans.find((p) => p.id === id);
				if (plan) onSelectPlan(plan);
			}}
			placeholder={textos.seleccionarPlan}
			ariaLabel={textos.selectorDePlan}
			isMenuOpen={isMenuOpen}
			summary={
				!unico && onSelectSummary
					? { label: textos.resumenDePlanes, active: isSummaryActive, onSelect: onSelectSummary }
					: undefined
			}
			addAction={onAddPlan ? { label: textos.anadirPlan, onSelect: onAddPlan } : undefined}
			// Con un solo plan y sin «Añadir plan» no hay nada que hacer al abrirlo.
			selectable={!unico || Boolean(onAddPlan)}
		/>
	);
};
